import { v4 as uuidv4 } from 'uuid';
import { RequestQuerySortTypesEnum } from '../../consts/enums';

/**
 * Add unique id for each item in the array
 * @param arr input array of any In type
 * @returns output array with `id` key added to each item
 */
function formatWithId<In>(arr?: In[]): (In & {id: string})[] {
  return arr?.map((item) => ({
    id: uuidv4(),
    ...item,
  })) || [];
}

/**
 * format array-string as an object with unique id
 * @param arr input array of strings
 * @returns output array of {id,value}
 */
const formatStringsWithId = (arr?: string[]): {id: string, value: string}[] => {
  return arr?.map((item) => ({
    id: uuidv4(),
    value: item,
  })) || [];
};

/**
 * format social-links with id and custom object
 * @param arr input array of social-links
 * @returns output array with social-link format
 */
const formatSocialLinksWithId = (arr?: SocialLinks[]): SocialLinkItem[] => {
  return arr?.map((item) => ({
    id: uuidv4(),
    socialType: item.type,
    url: item.url,
  })) || [];
};

export const formatTalentResponse = (data: TalentResponse): FormattedTalentResponse => {
  const dataCopy = JSON.parse(JSON.stringify(data));
  dataCopy.emails = formatStringsWithId(data.emails);
  dataCopy.phoneNumbers = formatStringsWithId(data.phoneNumbers);
  dataCopy.socialLinks = formatSocialLinksWithId(data.socialLinks);
  dataCopy.employmentHistory = formatWithId(data.employmentHistory);
  dataCopy.educationHistory = formatWithId(data.educationHistory);
  dataCopy.attributes = formatWithId(data.attributes);
  dataCopy.technicalSkills = formatWithId(data.technicalSkills);

  return dataCopy;
};

export const formatMatchResponse = (data: MatchResponse): MatchFormattedResponse => {
  const dataCopy = JSON.parse(JSON.stringify(data));
  dataCopy.talent = formatTalentResponse(dataCopy.talent);

  return dataCopy;
};

const mapping: Map<string, string> = new Map([
  ['rank', 'matchingRank'],
  ['talentSimilarityScore', 'matchingScoresAndRanks.talentSimilarityScore.value'],
  ['wtlScore', 'matchingScoresAndRanks.wtlScore.value'],
  ['layoffScore', 'matchingScoresAndRanks.layoffScore.value'],
  ['talentSemanticScore', 'matchingScoresAndRanks.talentSemanticScore.value'],
  ['companyHighestSemanticScore', 'matchingScoresAndRanks.companyHighestSemanticScore.value'],
]);

export function convertMatchCandidatesSort(newSort?: Sort): Sort {
  if (!newSort) {
    return {};
  }

  const newCandidatesSort: Sort = {};
  const ordersKey = Object.keys(newSort);
  ordersKey.forEach((key) => {
    const serverValue = mapping.get(key);
    if (!serverValue) {
      return;
    }
    newCandidatesSort[serverValue] = newSort[key];
  });

  // add as secondary sort
  if (!newCandidatesSort.matchingRank) {
    newCandidatesSort.matchingRank = RequestQuerySortTypesEnum.Ascendant;
  }
  
  return newCandidatesSort;
}
